// @ts-check
// Regras puras das estatísticas do bot (contrato de bots, seções 18 e 22.3): período (Hoje, 7 dias, 30 dias ou
// personalizado, na URL), as taxas (conclusão sem pessoa, transferência, abandono e inválidas por pergunta) e os
// números de cada bloco no formato do canvas.
import { flowStatsRange } from './flow-list.js';

/**
 * @typedef {{ started: number, completed: number, handedOff: number, timedOut: number, stopped: number, failed: number, ticketsOpened: number,
 *   cardsCreated: number }} BotStatsTotals
 * @typedef {{ arrived: number, passed: number, invalid: number, timeouts: number, failed: number, branches: Record<string, number> }} BotNodeStats
 * @typedef {{ period: { from: string, to: string }, totals: BotStatsTotals, series: Array<{ bucket: string } & BotStatsTotals>,
 *   nodes: Record<string, BotNodeStats>, endReasons: Record<string, number> }} BotStats
 */

export const BOT_STATS_PRESETS = Object.freeze(['TODAY', 'LAST_7_DAYS', 'LAST_30_DAYS', 'CUSTOM']);
export const BOT_STATS_VIEWS = Object.freeze(['nodes', 'runs']);
const MAXIMUM_STATS_DAYS = 366;

/** @param {string | null} value */
const isDay = (value) => Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value)));

/**
 * Período das estatísticas lido da URL: datas válidas (até 366 dias) ou 30 dias; a aba "Blocos" ou "Execuções".
 * @param {URLSearchParams} parameters @param {Date} now @param {string} timeZone
 * @returns {{ preset: string, from: string, to: string, view: 'nodes' | 'runs' }}
 */
export function readBotStatsPeriod(parameters, now, timeZone) {
  const view = parameters.get('view') === 'runs' ? 'runs' : 'nodes';
  const from = parameters.get('from');
  const to = parameters.get('to');
  if (isDay(from) && isDay(to) && /** @type {string} */ (from) <= /** @type {string} */ (to)) {
    const days = (Date.parse(/** @type {string} */ (to)) - Date.parse(/** @type {string} */ (from))) / 86_400_000 + 1;
    if (days <= MAXIMUM_STATS_DAYS) {
      const preset = BOT_STATS_PRESETS.find((candidate) => candidate !== 'CUSTOM'
        && flowStatsRange(candidate, now, timeZone).from === from && flowStatsRange(candidate, now, timeZone).to === to) ?? 'CUSTOM';
      return { preset, from: /** @type {string} */ (from), to: /** @type {string} */ (to), view };
    }
  }
  return { preset: 'LAST_30_DAYS', ...flowStatsRange('LAST_30_DAYS', now, timeZone), view };
}

/** Datas de um atalho de período. @param {string} preset @param {Date} now @param {string} timeZone */
export const botStatsRange = (preset, now, timeZone) => flowStatsRange(preset, now, timeZone);

/** Divisão protegida (zero sobre zero é nulo, mostrado como "-"). @param {number} part @param {number} whole */
export const rateOf = (part, whole) => (whole > 0 ? part / whole : null);

/**
 * Taxas do período: conclusão sem pessoa, transferência e abandono (sem resposta).
 * @param {Partial<BotStatsTotals> | null | undefined} totals
 */
export function botRates(totals) {
  const started = Number(totals?.started ?? 0);
  return {
    completedWithoutHuman: rateOf(Number(totals?.completed ?? 0), started),
    handedOff: rateOf(Number(totals?.handedOff ?? 0), started),
    abandoned: rateOf(Number(totals?.timedOut ?? 0), started),
  };
}

/** Inválidas sobre respostas de uma pergunta (passou mais inválidas). @param {Partial<BotNodeStats> | null | undefined} node */
export function invalidAnswerRate(node) {
  const invalid = Number(node?.invalid ?? 0);
  return rateOf(invalid, invalid + Number(node?.passed ?? 0));
}

/** Total de um ramo no período. @param {BotStats | null} stats @param {string} nodeId @param {string} branchKey */
export const branchCountOf = (stats, nodeId, branchKey) => Number(stats?.nodes?.[nodeId]?.branches?.[branchKey] ?? 0);

/**
 * Motivos de fim do período, do mais comum ao menos.
 * @param {Record<string, number> | null | undefined} endReasons
 * @returns {Array<{ reason: string, count: number }>}
 */
export function sortedEndReasons(endReasons) {
  return Object.entries(endReasons ?? {})
    .map(([reason, count]) => ({ reason, count: Number(count) }))
    .filter((entry) => entry.count > 0)
    .sort((first, second) => second.count - first.count || first.reason.localeCompare(second.reason));
}
